// Individual service page configurations. Each entry drives a route
// /services/:slug and the ServicePage.jsx component renders from this.
export const SERVICE_PAGES = {
  "lawn-care": {
    slug: "lawn-care",
    title: "Lawn Care",
    tagline: "Fertilize · Aerate · Edge · Maintain",
    hero: "https://images.unsplash.com/photo-1721138941961-cb245dd928af?auto=format&fit=crop&w=2000&q=80",
    season: "Summer",
    priceFrom: "From $135+ / month",
    description:
      "Full-service lawn care that keeps your turf showroom-ready all season. Fertilizing, aerating, overseeding and seasonal upkeep — handled on a recurring schedule with the same crew every visit.",
    highlights: [
      "Spring aeration + overseeding",
      "Seasonal fertilizing programs",
      "Edging & crisp bed lines",
      "Weed control (eco-conscious)",
      "Mulch top-ups on request",
      "Priority scheduling for monthly members",
    ],
    cta: "Book my lawn plan",
    theme: "default",
    related: ["lawn-mowing", "hedge-trimming", "backyard-makeover"],
  },
  "lawn-mowing": {
    slug: "lawn-mowing",
    title: "Lawn Mowing",
    tagline: "Precision cuts · Clean lines · Every week",
    hero: "https://images.unsplash.com/photo-1721138941961-cb245dd928af?auto=format&fit=crop&w=2000&q=80",
    season: "Summer",
    priceFrom: "From $45 / visit",
    description:
      "Weekly or bi-weekly mowing from a crew that cares about the details. Clean stripes, crisp edges, blown-out walkways and no clippings left behind — every single visit.",
    highlights: [
      "Weekly or bi-weekly schedule",
      "Precision stripes & clean edges",
      "Blown-off driveways, walks, porches",
      "Same crew, same day of the week",
      "Height adjusted per season",
      "Zero-turn + push mowers on hand",
    ],
    cta: "Start weekly mowing",
    theme: "default",
    related: ["lawn-care", "hedge-trimming", "backyard-makeover"],
  },
  "hedge-trimming": {
    slug: "hedge-trimming",
    title: "Hedge Trimming",
    tagline: "Crisp lines · Balanced shapes · Healthy growth",
    hero: "https://images.pexels.com/photos/32427439/pexels-photo-32427439.jpeg?auto=compress&cs=tinysrgb&w=2000",
    season: "Summer",
    priceFrom: "From $60 / visit",
    description:
      "Cedars, boxwoods, privets, yew — we shape them, maintain them and keep them healthy. Hand-finished cuts and full clean-up included on every visit.",
    highlights: [
      "Cedars, boxwoods, privets, yew",
      "Balanced symmetric shapes",
      "Hand-finished detail work",
      "Full clean-up & haul-away",
      "Spring + late-summer schedule",
      "Available as add-on or standalone",
    ],
    cta: "Book my hedge trim",
    theme: "default",
    related: ["tree-trimming", "lawn-care", "backyard-makeover"],
  },
  "tree-trimming": {
    slug: "tree-trimming",
    title: "Tree Trimming",
    tagline: "Safe pruning · Shape · Clean-up included",
    hero: "https://images.pexels.com/photos/6232013/pexels-photo-6232013.jpeg?auto=compress&cs=tinysrgb&w=2000",
    season: "Summer",
    priceFrom: "From $80 / tree",
    description:
      "Safe pruning for small-to-mid-sized trees. Shape, deadwood, raise canopies and keep branches off roofs and lines. Full brush haul-away included.",
    highlights: [
      "Small to mid-sized trees",
      "Crown shaping + deadwooding",
      "Canopy raising",
      "Full brush haul-away",
      "Insured climbers & pole saws",
      "Large/complex jobs quoted on site",
    ],
    cta: "Quote my tree job",
    theme: "default",
    related: ["hedge-trimming", "junk-removal", "backyard-makeover"],
  },
  patios: {
    slug: "patios",
    title: "Patios & Interlock",
    tagline: "Pavers · Walkways · Repair · Sealing",
    hero: "https://images.pexels.com/photos/36779942/pexels-photo-36779942.jpeg?auto=compress&cs=tinysrgb&w=2000",
    season: "Summer",
    priceFrom: "Quoted per project",
    description:
      "Interlock walkways, paver patios, and stonework refresh. Fix lifted stones, re-level, polymeric sand, seal and bring tired hardscapes back to life.",
    highlights: [
      "Interlock paver walkways",
      "Patio repair & re-leveling",
      "Polymeric sand & jointing",
      "Sealing & protection",
      "Steps & retaining-wall touch-ups",
      "Residential + commercial",
    ],
    cta: "Get a patio quote",
    theme: "default",
    related: ["backyard-makeover", "lawn-care", "junk-removal"],
  },
  "junk-removal": {
    slug: "junk-removal",
    title: "Junk Removal",
    tagline: "Loaded · Hauled · Gone — same day",
    hero: "https://images.pexels.com/photos/7464689/pexels-photo-7464689.jpeg?auto=compress&cs=tinysrgb&w=2000",
    season: "Year-round",
    priceFrom: "From $99 / load",
    description:
      "Old furniture, mattresses, appliances, yard waste, construction debris, hoarder clean-outs, estate clean-outs. We show up, lift the heavy stuff, sweep up, and dispose of it properly.",
    highlights: [
      "Furniture, mattresses, appliances",
      "Renovation & construction debris",
      "Yard waste, tree limbs, soil",
      "Estate & hoarder clean-outs",
      "Garage & basement clean-outs",
      "Same-day pickup available",
    ],
    cta: "Call for same-day pickup",
    theme: "default",
    related: ["backyard-makeover", "tree-trimming", "bin-washing"],
  },
  "backyard-makeover": {
    slug: "backyard-makeover",
    title: "Backyard Makeovers",
    tagline: "End-to-end property transformations",
    hero: "https://images.unsplash.com/photo-1759602602326-a4e9646514a6?auto=format&fit=crop&w=2000&q=80",
    season: "Summer",
    priceFrom: "Quoted per project",
    description:
      "Full backyard transformations. Fresh sod, mulched beds, planters, hedges, lighting, patio refresh and finishing touches — we plan it, deliver it and clean up after.",
    highlights: [
      "Design & walkthrough",
      "Fresh sod + irrigation prep",
      "Mulched beds + planting",
      "Interlock + patio integration",
      "Lighting & finishing touches",
      "Project management end-to-end",
    ],
    cta: "Transform my backyard",
    theme: "default",
    related: ["patios", "lawn-care", "hedge-trimming"],
  },
  "bin-washing": {
    slug: "bin-washing",
    title: "Garbage Bin Washing",
    tagline: "Curbside hot-wash · Sanitize · Deodorize",
    hero: "https://images.pexels.com/photos/36086364/pexels-photo-36086364.jpeg?auto=compress&cs=tinysrgb&w=2000",
    season: "Year-round",
    priceFrom: "From $25 / bin",
    description:
      "Curbside hot-water wash, sanitize and deodorize — your bins come back home clean, odor-free and ready for the week. One-time or recurring.",
    highlights: [
      "Hot-water pressure wash",
      "Eco-safe sanitizer + deodorizer",
      "Recurring plans available",
      "Curbside pickup — no effort",
      "Residential + commercial",
      "Great add-on to monthly lawn plan",
    ],
    cta: "Book a bin wash",
    theme: "default",
    related: ["junk-removal", "lawn-mowing"],
  },
  "snow-removal": {
    slug: "snow-removal",
    title: "Snow Removal",
    tagline: "Driveways · Walkways · Entries · Automatic",
    hero: "https://images.unsplash.com/photo-1704578424157-223c3ad72870?auto=format&fit=crop&w=2000&q=80",
    season: "Winter",
    priceFrom: "Residential from $800+ / season · plus salt fee",
    description:
      "Automatic residential snow removal for driveways, walkways and entries — cleared before the morning rush. Automatically triggered at 2 inches or more. Salt fee is quoted separately based on property size and application frequency.",
    highlights: [
      "Automatic weather-triggered service",
      "Driveways + walkways + entries",
      "Cleared before 7 AM when possible",
      "Shovel-finish on steps & landings",
      "Salting / de-icing billed separately",
      "Lock in your season rate",
    ],
    cta: "Reserve my winter plan",
    theme: "winter",
    related: ["snow-plow", "salting", "junk-removal"],
  },
  salting: {
    slug: "salting",
    title: "Salting & De-Icing",
    tagline: "Eco-conscious · Safe surfaces · Storm priority",
    hero: "https://images.pexels.com/photos/35826495/pexels-photo-35826495.jpeg?auto=compress&cs=tinysrgb&w=2000",
    season: "Winter",
    priceFrom: "Add-on from $45 / application",
    description:
      "Keep families, customers and employees safe. Eco-conscious salt and de-icing applied to driveways, walkways and commercial lots — storm-priority response.",
    highlights: [
      "Driveways + walks + entries",
      "Parking lots + commercial",
      "Eco-friendly de-icer options",
      "Storm-priority response",
      "Available on every winter plan",
      "Residential + commercial contracts",
    ],
    cta: "Add salting to my plan",
    theme: "winter",
    related: ["snow-removal", "snow-plow"],
  },
  "snow-plow": {
    slug: "snow-plow",
    title: "Snow Plowing",
    tagline: "Commercial-grade · Long driveways · Lots",
    hero: "https://images.unsplash.com/photo-1676363707718-eabe464ca9ae?auto=format&fit=crop&w=2000&q=80",
    season: "Winter",
    priceFrom: "From $1,800 / season",
    description:
      "Commercial-grade plowing for long laneways, estates and parking lots across the Niagara Region. Priority queue — your property is cleared first.",
    highlights: [
      "Long driveways + estates",
      "Commercial parking lots",
      "Priority clearing queue",
      "Multiple entrance points",
      "Salting / de-icing included",
      "Storm-by-storm reporting",
    ],
    cta: "Book plow service",
    theme: "winter",
    related: ["snow-removal", "salting"],
  },
};
